'use client';

import { useState, useEffect } from 'react';
import { automationId } from '@terra/shared-utils';
import { Search01Icon, Cancel01Icon } from '@terra/ui-icons';
import { Box, HStack, token } from '@terra/ui-styled-system';
import { Filter, type FilterProps } from './filter';
import { Input } from './input';
import { useTranslations } from 'next-intl';
import { EmptyState } from '../custom-components/empty-state/EmptyState';

export type FilterValue = Array<{
  id: string;
  value: string[];
}>;
export type FilterConfig = Omit<FilterProps, 'onChange' | 'value'>;
export type FilterBarProps = {
  filters: FilterConfig[];
  filtersAfterSearch?: FilterConfig[];
  leadingCustomFilters?: React.ReactNode;
  customFilters?: React.ReactNode;
  value: FilterValue;
  onFilterChange: (value: FilterValue) => void;
  onSearch?: (value: string) => void;
  searchPlaceholder?: string;
  rightContent?: React.ReactNode;
  searchValue?: string;
  searchWidth?: string;
  dataTestid?: string;
  px?: number;
  pb?: number;
  pt?: number;
  showBottomBorder?: boolean;
  wrapFilters?: boolean;
};
export const FilterBar = ({
  filters,
  filtersAfterSearch = [],
  leadingCustomFilters,
  customFilters,
  value: selectedFilterEntries,
  onFilterChange,
  onSearch,
  searchPlaceholder,
  rightContent,
  searchValue: controlledSearchValue,
  searchWidth = '280px',
  dataTestid = '',
  showBottomBorder = true,
  px = 8,
  pb = 6,
  pt = 0,
  wrapFilters = true,
}: FilterBarProps) => {
  const t = useTranslations('designSystem.filterBar');
  const [internalSearchValue, setInternalSearchValue] = useState(
    controlledSearchValue ?? '',
  );
  const isSearchControlled = controlledSearchValue !== undefined;
  const searchValue = isSearchControlled
    ? controlledSearchValue
    : internalSearchValue;

  useEffect(() => {
    if (isSearchControlled) {
      setInternalSearchValue(controlledSearchValue);
    }
  }, [isSearchControlled, controlledSearchValue]);
  const handleFilterChange = (
    filterId: string,
    nextSelectedOptions: string[],
  ) => {
    const otherFilterEntries = selectedFilterEntries.filter(
      (entry) => entry.id !== filterId,
    );
    const nextSelectedFilterEntries =
      nextSelectedOptions.length > 0
        ? [...otherFilterEntries, { id: filterId, value: nextSelectedOptions }]
        : otherFilterEntries;

    onFilterChange(nextSelectedFilterEntries);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    if (!isSearchControlled) {
      setInternalSearchValue(newValue);
    }

    onSearch?.(newValue);
  };

  const handleClearSearch = () => {
    if (!isSearchControlled) {
      setInternalSearchValue('');
    }

    onSearch?.('');
  };

  const searchField = onSearch ? (
    <Box
      as="label"
      position="relative"
      w={searchWidth}
      minW={searchWidth}
      maxW={searchWidth}
      flexShrink={0}
      style={{
        maxWidth: searchWidth,
        width: searchWidth,
      }}
    >
      <Search01Icon
        size={16}
        color={token('colors.fg.neutral.subtle')}
        style={{
          position: 'absolute',
          left: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Input
        value={searchValue}
        onChange={handleSearchChange}
        placeholder={searchPlaceholder ?? t('searchPlaceholder')}
        size="xs"
        cursor="pointer"
        pl="10"
        pr="8"
        borderRadius="full"
        w="full"
        bg="bg.neutral.default"
        autoComplete="off"
        name="integrations-filter-search"
        {...automationId(dataTestid, 'search')}
        _hover={{ bg: 'bg.neutral.subtleSoft' }}
      />
      {searchValue && (
        <Box
          as="button"
          // @ts-expect-error - Box with as="button" doesn't properly type button props
          type="button"
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="absolute"
          right="8px"
          top="50%"
          transform="translateY(-50%)"
          borderRadius="full"
          w="20px"
          h="20px"
          bg="bg.neutral.subtle"
          color="fg.default"
          cursor="pointer"
          _hover={{
            bg: 'bg.neutral.subtleSoft',
          }}
          aria-label="Clear search"
          onClick={handleClearSearch}
        >
          <Cancel01Icon size={14} color={token('colors.fg.default')} />
        </Box>
      )}
    </Box>
  ) : null;

  return (
    <Box w="full" px={px} pb={pb} pt={pt}>
      <HStack
        w="full"
        alignItems={wrapFilters ? 'flex-start' : 'center'}
        justifyContent="space-between"
        gap={2.5}
      >
        <HStack
          gap={2.5}
          alignItems="center"
          flexGrow={1}
          flexWrap={wrapFilters ? 'wrap' : 'nowrap'}
          overflowX={wrapFilters ? undefined : 'auto'}
          minW={0}
        >
          {leadingCustomFilters ? (
            <Box flexShrink={0}>{leadingCustomFilters}</Box>
          ) : null}
          {searchField}
          {[...filtersAfterSearch, ...filters].map((filter) => {
            const selectedOptionsForFilter = selectedFilterEntries.find(
              (entry) => entry.id === filter.id,
            )?.value;

            return (
              <Filter
                key={filter.id}
                {...filter}
                value={selectedOptionsForFilter}
                onChange={(nextSelectedOptions) =>
                  handleFilterChange(filter.id, nextSelectedOptions)
                }
              />
            );
          })}
          {customFilters}
        </HStack>
        {rightContent ? (
          <HStack gap={2.5} alignItems="center" flexShrink={0} h="32px">
            {rightContent}
          </HStack>
        ) : null}
      </HStack>
      {showBottomBorder && (
        <Box mt={4} w="full" h="1px" bg="border.neutral.subtle" />
      )}
    </Box>
  );
};

export const FilterBarEmptyState = () => {
  const t = useTranslations('designSystem.filterBar.emptyState');

  return (
    <EmptyState
      title={t('title')}
      description={t('description')}
      icon={
        <Search01Icon size={24} color={token('colors.fg.neutral.disabled')} />
      }
    />
  );
};
